import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Search, ArrowRight, X } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";
import { useCompoundSearchIndex } from "@/hooks/useDatabase";

type SearchItem = {
  slug: string;
  name: string;
  aliases: string[];
  category: string | null;
  description: string | null;
  effects: string[];
  source: "nootropic" | "compound";
  experience_level?: string | null;
};

function normalize(s: string) {
  return s.toLowerCase().replace(/[-_\s]+/g, " ").trim();
}

// The index loads when the visitor first focuses or types in the box, not with the page. Compounds come from the
// light search index the navbar shares (slug, name, aliases, category: about 26 KB). Their descriptions are no
// longer part of it: they made this request 580 KB on every homepage view, so a word that only appears in a
// compound's description no longer finds it.
function useCombinedCompounds(enabled: boolean) {
  const { data: compounds, isPending: compoundsPending } = useCompoundSearchIndex(enabled);
  const { data: nootRows, isPending: nootsPending } = useQuery({
    queryKey: ["combined-compound-search-noots"],
    staleTime: 1000 * 60 * 30,
    enabled,
    queryFn: async () => {
      // 2026-04-29: removed `aliases` from this select: nootropic_compounds
      // doesn't have an aliases column, so the request was returning 400
      // ("column nootropic_compounds.aliases does not exist") on every
      // homepage load. The .map below already defaults aliases to [] when
      // the field is missing, so dropping it from select is safe.
      const { data } = await supabase
        .from("nootropic_compounds")
        .select("slug, name, category, description, effects, experience_level")
        .order("name");
      return (data || []) as any[];
    },
  });

  const data = useMemo((): SearchItem[] => {
    const noots: SearchItem[] = (nootRows || []).map((r) => ({
      slug: r.slug,
      name: r.name,
      aliases: Array.isArray(r.aliases) ? r.aliases : [],
      category: r.category ?? null,
      description: r.description ?? null,
      effects: Array.isArray(r.effects) ? r.effects : [],
      source: "nootropic",
      experience_level: r.experience_level ?? null,
    }));

    const nootSlugs = new Set(noots.map((n) => n.slug));
    const comps: SearchItem[] = (compounds || [])
      .filter((r) => !nootSlugs.has(r.slug))
      .map((r) => ({
        slug: r.slug,
        name: r.name,
        aliases: Array.isArray(r.aliases) ? r.aliases : [],
        category: r.category ?? null,
        description: null,
        effects: [],
        source: "compound",
      }));

    return [...noots, ...comps];
  }, [compounds, nootRows]);

  return { data, isPending: compoundsPending || nootsPending };
}

export default function CompoundSearchCombobox({
  placeholder = "Search compounds: Semax, BPC-157, Lion's Mane",
  onQueryChange,
  autoFocus = false,
  size = "md",
  inputId,
}: {
  placeholder?: string;
  onQueryChange?: (q: string) => void;
  autoFocus?: boolean;
  /** "lg" is the homepage hero box: 56px tall, 16px text, a white card with a soft blue shadow and a blue search icon
   *  (direction B, 2026-10-24; .home-card in index.css). The static shell in index.html draws the same box before the
   *  page's scripts run, so keep its size and padding in step with the shell. */
  size?: "md" | "lg";
  /** Optional id for the input, so a page can focus it from a keyboard shortcut. */
  inputId?: string;
}) {
  const navigate = useNavigate();
  // Set on the first focus or keystroke; the index is not requested before that.
  const [indexWanted, setIndexWanted] = useState(autoFocus);
  const { data: items, isPending: indexPending } = useCombinedCompounds(indexWanted);
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    onQueryChange?.(query);
  }, [query, onQueryChange]);

  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (!wrapperRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const results = useMemo(() => {
    const q = normalize(query);
    if (!q) return [] as Array<SearchItem & { score: number }>;
    const tokens = q.split(" ").filter(Boolean);
    const scored = items
      .map((item) => {
        const name = normalize(item.name);
        const slug = normalize(item.slug);
        const aliasBlob = normalize((item.aliases || []).join(" "));
        const cat = normalize(item.category || "");
        const desc = normalize(item.description || "");
        const effects = normalize((item.effects || []).join(" "));
        const haystack = [name, slug, aliasBlob, cat, desc, effects].join(" ");
        if (!tokens.every((t) => haystack.includes(t))) return null;

        let score = 0;
        if (name === q) score += 1000;
        if (name.startsWith(q)) score += 500;
        if (slug.startsWith(q)) score += 400;
        if (aliasBlob.split(" ").some((a) => a === q)) score += 300;
        if (name.includes(q)) score += 100;
        if (aliasBlob.includes(q)) score += 80;
        if (cat.includes(q)) score += 40;
        if (effects.includes(q)) score += 20;
        if (desc.includes(q)) score += 5;
        if (item.source === "nootropic") score += 3;
        return { ...item, score };
      })
      .filter(Boolean) as Array<SearchItem & { score: number }>;

    scored.sort((a, b) => b.score - a.score);
    return scored.slice(0, 8);
  }, [items, query]);

  useEffect(() => {
    if (activeIdx >= results.length) setActiveIdx(0);
  }, [results.length, activeIdx]);

  const go = (item: SearchItem) => {
    const path = item.source === "nootropic" ? `/nootropic/${item.slug}` : `/compound/${item.slug}`;
    setOpen(false);
    setQuery("");
    navigate(path);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setOpen(true);
      setActiveIdx((i) => Math.min(i + 1, Math.max(results.length - 1, 0)));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      if (results[activeIdx]) {
        e.preventDefault();
        go(results[activeIdx]);
      } else if (query.trim()) {
        e.preventDefault();
        setOpen(false);
        navigate(`/nootropics/compounds?q=${encodeURIComponent(query.trim())}`);
      }
    } else if (e.key === "Escape") {
      setOpen(false);
      inputRef.current?.blur();
    }
  };

  const showDropdown = open && query.trim().length > 0;

  return (
    <div ref={wrapperRef} className="relative">
      <Search className={cn("absolute top-1/2 -translate-y-1/2 pointer-events-none z-10", size === "lg" ? "left-4 h-5 w-5 text-primary" : "left-3 sm:left-4 h-4 w-4 text-muted-foreground")} />
      <input
        ref={inputRef}
        type="text"
        role="combobox"
        aria-expanded={showDropdown}
        aria-controls="compound-search-results"
        autoComplete="off"
        spellCheck={false}
        autoFocus={autoFocus}
        placeholder={placeholder}
        value={query}
        onChange={(e) => {
          setIndexWanted(true);
          setQuery(e.target.value);
          setOpen(true);
          setActiveIdx(0);
        }}
        onFocus={() => {
          setIndexWanted(true);
          if (query.trim()) setOpen(true);
        }}
        onKeyDown={onKeyDown}
        id={inputId}
        className={size === "lg" ? "home-card home-card-blue w-full h-14 pl-12 pr-12 text-base text-foreground placeholder:text-muted-foreground focus:[--card-ring:hsl(var(--primary))]" :"w-full h-10 sm:h-12 pl-10 sm:pl-11 pr-10 rounded-xl sm:rounded-2xl bg-card/80 border border-border/40 backdrop-blur-sm text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary/50 focus:ring-2 focus:ring-primary/20 transition-colors"}
      />
      {query && (
        <button
          type="button"
          aria-label="Clear search"
          onClick={() => {
            setQuery("");
            setOpen(false);
            inputRef.current?.focus();
          }}
          className="absolute right-1.5 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-control text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <X aria-hidden className="h-4 w-4" />
        </button>
      )}

      {showDropdown && (
        <div
          id="compound-search-results"
          role="listbox"
          className="absolute left-0 right-0 top-[calc(100%+6px)] z-modal overflow-hidden rounded-xl border border-border bg-popover text-left shadow-elevated"
        >
          {indexPending ? (
            <div className="type-small flex items-center gap-2 p-4 text-muted-foreground">
              <span aria-hidden className="h-3 w-3 animate-spin rounded-full border border-primary/40 border-t-primary" />
              Loading the compound index
            </div>
          ) : results.length === 0 ? (
            <div className="p-4">
              <p className="type-small mb-1 text-muted-foreground">
                No compounds matched <span className="font-semibold text-foreground">"{query}"</span>
              </p>
              <button
                type="button"
                onClick={() => {
                  setOpen(false);
                  navigate(`/wiki?q=${encodeURIComponent(query.trim())}`);
                }}
                className="type-small inline-flex min-h-11 items-center gap-1.5 font-medium text-primary underline-offset-4 hover:underline"
              >
                Search the full wiki <ArrowRight aria-hidden className="h-4 w-4" />
              </button>
            </div>
          ) : (
            <>
              <ul className="max-h-[360px] overflow-y-auto py-1">
                {results.map((item, i) => {
                  const active = i === activeIdx;
                  const meta = [item.source === "nootropic" ? "Nootropic" : "Compound", item.category].filter(Boolean).join(", ");
                  return (
                    <li key={`${item.source}-${item.slug}`} role="option" aria-selected={active}>
                      <button
                        type="button"
                        onMouseEnter={() => setActiveIdx(i)}
                        onClick={() => go(item)}
                        className={cn(
                          "flex min-h-11 w-full items-start gap-3 px-4 py-2.5 text-left transition-colors duration-150",
                          active ? "bg-secondary" : "hover:bg-secondary/60",
                        )}
                      >
                        <span className="min-w-0 flex-1">
                          <span className="type-small block truncate font-semibold text-foreground">{item.name}</span>
                          <span className="type-label block truncate font-normal text-muted-foreground">{meta}</span>
                          {(item.effects?.length || item.description) && (
                            <span className="type-label mt-0.5 line-clamp-1 font-normal text-muted-foreground">
                              {item.effects?.length ? item.effects.slice(0, 3).join(", ") : (item.description || "").slice(0, 90)}
                            </span>
                          )}
                        </span>
                        <ArrowRight aria-hidden className={cn("mt-0.5 h-4 w-4 shrink-0", active ? "text-foreground" : "text-transparent")} />
                      </button>
                    </li>
                  );
                })}
              </ul>
              <div className="flex items-center justify-between gap-3 border-t border-border bg-secondary/50 px-4">
                <span className="type-label hidden font-normal text-muted-foreground sm:inline">
                  Arrow keys to move,{" "}
                  <kbd className="rounded border border-border bg-background px-1 font-mono text-xs text-foreground">Enter</kbd> to open,{" "}
                  <kbd className="rounded border border-border bg-background px-1 font-mono text-xs text-foreground">Esc</kbd> to close
                </span>
                <button
                  type="button"
                  onClick={() => {
                    setOpen(false);
                    navigate(`/nootropics/compounds?q=${encodeURIComponent(query.trim())}`);
                  }}
                  className="type-small inline-flex min-h-11 items-center gap-1 font-medium text-primary underline-offset-4 hover:underline"
                >
                  See all results <ArrowRight aria-hidden className="h-4 w-4" />
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
